import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, FileText, Calendar, User, Clock, CheckCircle, AlertCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { isGestorParceiroRole, isPartnerScopedRole } from '../../lib/authorization';
import CreateServiceOrderModal from './components/CreateServiceOrderModal';

const AdminServiceOrders = ({ partner, auth }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [serviceOrders, setServiceOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [selectedOS, setSelectedOS] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsPerPageOptions = [5, 10, 20, 50, 100];

  const fetchServiceOrders = async () => {
    setLoading(true);
    try {
      const p = auth?.permissionsJson || {};
      const isSuperAdmin = auth?.isAdmin;
      const canManageAll = isSuperAdmin || p.manage_all_os;
      const canViewCompanyOrders = isGestorParceiroRole(auth?.profileRole);
      
      const user = await supabase.auth.getUser();
      const userId = user?.data?.user?.id;
      
      let query = supabase.from('service_orders').select('*').order('created_at', { ascending: false });
      
      if (!canManageAll && !canViewCompanyOrders && userId) {
        query = query.eq('created_by', userId);
      }
      
      const { data: orders, error: ordersError } = await query;
      
      if (ordersError) throw ordersError;

      if (orders && orders.length > 0) {
        // 2. Fetch Creators (Profiles)
        const userIds = [...new Set(orders.map(o => o.created_by).filter(Boolean))];
        
        if (userIds.length > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, full_name, sas_partner:sas_partners(name)')
            .in('id', userIds);
            
          if (profilesError) throw profilesError;

          // Map profiles to orders
          const profilesMap = profiles.reduce((acc, p) => {
            acc[p.id] = {
              name: p.full_name,
              partner: p.sas_partner?.name
            };
            return acc;
          }, {});

          const ordersWithCreators = orders.map(o => ({
            ...o,
            creator_info: profilesMap[o.created_by] || null
          }));

          setServiceOrders(ordersWithCreators);
          return ordersWithCreators;
        } else {
          setServiceOrders(orders);
          return orders;
        }
      } else {
        setServiceOrders([]);
        return [];
      }

    } catch (error) {
      console.error('Erro ao buscar OS:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const orders = await fetchServiceOrders();
      try {
        const params = new URLSearchParams(window.location.search || '');
        const osId = params.get('os_id') || params.get('osId') || params.get('os');
        const osNumberRaw = params.get('os_number') || params.get('osNumber') || params.get('numeroOS');
        const osNumber = osNumberRaw ? Number(osNumberRaw) : null;

        if (!osId && !osNumber) return;

        let picked = null;
        if (Array.isArray(orders)) {
          if (osId) picked = orders.find(o => o?.id === osId) || null;
          if (!picked && osNumber && Number.isFinite(osNumber)) {
            picked = orders.find(o => Number(o?.os_number) === osNumber) || null;
          }
        }

        if (!picked) {
          let q = supabase.from('service_orders').select('*').limit(1);
          if (osId) q = q.eq('id', osId);
          else q = q.eq('os_number', osNumber);

          const { data, error } = await q.maybeSingle();
          if (!error) picked = data;
        }

        if (picked) {
          setSelectedOS(picked);
          setShowCreateModal(true);
        }

        window.history.replaceState({}, '', window.location.pathname);
      } catch {
        return;
      }
    })();
  }, []);

  const handleCreateOS = () => {
    setSelectedOS(null);
    setShowCreateModal(true);
  };

  const handleDetails = (os) => {
    setSelectedOS(os);
    setShowCreateModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Criada': return 'text-slate-300 bg-slate-500/20 border-slate-500/30';
      case 'Em análise': return 'text-blue-300 bg-blue-500/20 border-blue-500/30';
      case 'Em andamento': return 'text-yellow-300 bg-yellow-500/20 border-yellow-500/30';
      case 'Aguardando informações': return 'text-orange-300 bg-orange-500/20 border-orange-500/30';
      case 'Concluída': return 'text-teal-300 bg-teal-500/20 border-teal-500/30';
      case 'Cancelada': return 'text-red-300 bg-red-500/20 border-red-500/30';
      default: return 'text-slate-300 bg-slate-500/20 border-slate-500/30';
    }
  };

  const filteredOrders = serviceOrders.filter(os => {
    const matchesSearch = 
      (os.company_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (os.id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (String(os.os_number) || '').includes(searchTerm.toLowerCase()) ||
      (os.focal_point_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (os.additional_focal_points?.some(fp => (fp.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())));
    
    const matchesStatus = statusFilter === 'Todos' || os.status === statusFilter;
    
    let matchesDate = true;
    if (startDate || endDate) {
      const orderDate = new Date(os.created_at).toISOString().split('T')[0];
      if (startDate && orderDate < startDate) matchesDate = false;
      if (endDate && orderDate > endDate) matchesDate = false;
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const totalRequestedEnrollments = filteredOrders.reduce(
    (sum, os) => sum + (Number(os?.quantity) || 0),
    0
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, startDate, endDate, itemsPerPage]);

  return (
    <div className="p-4 md:p-8 pb-20 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Gestão de OS</h1>
          <p className="text-slate-400">
            {auth?.profile?.role === 'Parceiro SAS'
              ? 'Gerencie suas solicitações e ordens de serviço.' 
              : auth?.profile?.role === 'Gestor Parceiro'
                ? 'Gerencie todas as ordens de serviço vinculadas às empresas sob sua gestão.'
                : 'Gerencie as ordens de serviço e solicitações de manutenção.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-stretch md:self-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 w-[190px] flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-800/80 border border-slate-700 flex items-center justify-center text-orange-400 shrink-0">
              <FileText size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Total de OS</div>
              <div className="text-xl font-bold text-white leading-tight">{filteredOrders.length}</div>
            </div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 w-[190px] flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-800/80 border border-slate-700 flex items-center justify-center text-teal-400 shrink-0">
              <User size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Matrículas</div>
              <div className="text-xl font-bold text-white leading-tight">{totalRequestedEnrollments}</div>
            </div>
          </div>
          <button 
            onClick={handleCreateOS} 
            style={{ backgroundColor: partner?.color || '#f97316' }} 
            className="text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition"
          >
            <Plus size={20} /> Criar OS
          </button>
        </div>
      </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
            <input 
              type="text" 
              placeholder="Buscar por empresa, ID ou solicitante..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 p-4 pl-12 rounded-xl text-white focus:border-orange-500 outline-none transition"
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Filter */}
            <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 p-2 rounded-xl">
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase px-1">Início</label>
                <input 
                  type="date" 
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent text-xs text-white outline-none [color-scheme:dark] px-1"
                />
              </div>
              <div className="w-px h-8 bg-slate-800 mx-1"></div>
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase px-1">Fim</label>
                <input 
                  type="date" 
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent text-xs text-white outline-none [color-scheme:dark] px-1"
                />
              </div>
              {(startDate || endDate) && (
                <button 
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="p-1 hover:text-rose-500 text-slate-500 transition"
                  title="Limpar Datas"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 p-4 rounded-xl text-white">
              <Filter size={20} className="text-slate-500" />
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-900 text-white outline-none cursor-pointer [color-scheme:dark]"
              >
                <option value="Todos" style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>Todos os Status</option>
                <option value="Criada" style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>Criada</option>
                <option value="Em análise" style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>Em análise</option>
                <option value="Em andamento" style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>Em andamento</option>
                <option value="Aguardando informações" style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>Aguardando informações</option>
                <option value="Cancelada" style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>Cancelada</option>
                <option value="Concluída" style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>Concluída</option>
              </select>
            </div>
          </div>
        </div>

      <div className="grid gap-4">
        {loading ? (
           <div className="text-center py-8 text-slate-500">Carregando ordens de serviço...</div>
        ) : paginatedOrders.length === 0 ? (
           <div className="text-center py-8 text-slate-500">Nenhuma OS encontrada.</div>
        ) : (
          <>
            {paginatedOrders.map((os) => (
              <div key={os.id} className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition group">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-mono font-bold text-white bg-orange-600/20 px-2 py-1 rounded border border-orange-600/30">
                        OS #{os.os_number}
                      </span>
                      <span className={`text-[11px] uppercase font-bold px-2.5 py-1 rounded-full border shadow-sm ${getStatusColor(os.status)}`}>
                        {os.status}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock size={12} /> {new Date(os.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1 group-hover:text-teal-400 transition">{os.company_name}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                      {os.creator_info && !isPartnerScopedRole(auth?.profile?.role) && (
                        <>
                          <span className="flex items-center gap-1">
                            Solicitante: <span className="text-white font-medium">{os.creator_info.name}</span>
                          </span>
                          {os.creator_info.partner && (
                            <span className="flex items-center gap-1">
                              Parceiro SAS: <span className="text-orange-400 font-medium">{os.creator_info.partner}</span>
                            </span>
                          )}
                          <span className="text-slate-700">|</span>
                        </>
                      )}
                      
                      {isPartnerScopedRole(auth?.profile?.role) && (os.desired_start_date || os.desired_end_date) && (
                        <>
                          {os.desired_start_date && (
                            <span className="flex items-center gap-1 bg-slate-800/50 px-2 py-0.5 rounded border border-slate-700">
                              <Calendar size={12} className="text-teal-400" />
                              Início: <span className="text-teal-300 font-bold">{new Date(os.desired_start_date + 'T00:00:00').toLocaleDateString()}</span>
                            </span>
                          )}
                          {os.desired_end_date && (
                            <span className="flex items-center gap-1 bg-slate-800/50 px-2 py-0.5 rounded border border-slate-700">
                              <Calendar size={12} className="text-rose-400" />
                              Fim: <span className="text-rose-300 font-bold">{new Date(os.desired_end_date + 'T00:00:00').toLocaleDateString()}</span>
                            </span>
                          )}
                          <span className="text-slate-700">|</span>
                        </>
                      )}

                      <span className="flex items-center gap-1"><User size={12} className="text-slate-500" /> {os.focal_point_name} {os.additional_focal_points?.length > 0 && `(+${os.additional_focal_points.length})`}</span>
                      <span className="flex items-center gap-1"><FileText size={12} className="text-slate-500" /> Qtd: {os.quantity}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleDetails(os)}
                      className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition"
                    >
                      Detalhes
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination Controls */}
            {filteredOrders.length > 0 && (
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-slate-800">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 uppercase font-bold">Itens por página:</span>
                  <select 
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="bg-slate-900 border border-slate-800 text-slate-300 text-xs rounded-lg px-2 py-1 outline-none focus:border-orange-500 transition"
                  >
                    {itemsPerPageOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <span className="text-xs text-slate-500 ml-2">
                    Mostrando {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredOrders.length)} de {filteredOrders.length}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 bg-slate-900 border border-slate-800 rounded-lg text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNum = i + 1;
                      // Show first, last, and pages around current
                      if (
                        pageNum === 1 || 
                        pageNum === totalPages || 
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button 
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 rounded-lg text-xs font-bold transition ${currentPage === pageNum ? 'bg-orange-500 text-white' : 'bg-slate-900 text-slate-500 hover:text-white border border-slate-800'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 || 
                        pageNum === currentPage + 2
                      ) {
                        return <span key={pageNum} className="text-slate-700 px-1">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-slate-900 border border-slate-800 rounded-lg text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CreateServiceOrderModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchServiceOrders}
        partner={partner}
        existingOS={selectedOS}
      />
    </div>
  );
};

export default AdminServiceOrders;
